import React from 'react';
import styled from 'styled-components';
import Play from '../assets/play-big.svg';
import Stop from '../assets/stop.svg';
import media from '../style/media';
import { theme } from '../style/theme';
import Spinner from './Spinner';

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
  width: 80px;
  ${media.tablet`
    height: 120px;
    width: 120px;
  `}
`;

const ProgressRing = styled.svg<{ progress: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  z-index: 1;
`;

const ProgressCircle = styled.circle<{ 
  progress: number; 
  circumference: number; 
  isMeasuring: boolean;
}>`
  fill: none;
  stroke: ${({ isMeasuring }) => 
    isMeasuring ? theme.colors.primary.main : 'transparent'};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: ${({ circumference }) => circumference};
  stroke-dashoffset: ${({ progress, circumference }) => 
    circumference - (progress / 100) * circumference};
  transition: stroke-dashoffset ${theme.transitions.normal}, 
              stroke ${theme.transitions.normal};
  opacity: ${({ isMeasuring }) => isMeasuring ? 1 : 0};
`;

const BackgroundCircle = styled.circle`
  fill: none;
  stroke: ${theme.colors.primary.light};
  stroke-width: 2;
  opacity: 0.3;
`;

const Button = styled.div<{ isMeasuring: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ isMeasuring }) => 
    isMeasuring 
      ? theme.colors.status.error 
      : theme.colors.primary.main};
  border-radius: 50%;
  cursor: pointer;
  height: 64px;
  width: 64px;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.normal};
  z-index: 2;
  
  ${media.tablet`
    height: 96px;
    width: 96px;
  `}
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: ${({ isMeasuring }) => 
      isMeasuring 
        ? `0 20px 25px -5px rgba(239, 68, 68, 0.4), 0 10px 10px -5px rgba(239, 68, 68, 0.1)`
        : theme.shadows.primary};
  }
  
  &:active {
    transform: translateY(0) scale(1.02);
    box-shadow: ${theme.shadows.md};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: ${({ isMeasuring }) => 
      isMeasuring 
        ? `linear-gradient(135deg, ${theme.colors.status.error}20, ${theme.colors.status.error}10)`
        : `linear-gradient(135deg, ${theme.colors.primary.main}20, ${theme.colors.primary.main}10)`};
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity ${theme.transitions.normal};
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const Icon = styled.img<{ isMeasuring: boolean }>`
  width: ${({ isMeasuring }) => isMeasuring ? '24px' : '28px'};
  height: ${({ isMeasuring }) => isMeasuring ? '24px' : '28px'};
  filter: brightness(0) invert(1);
  transition: all ${theme.transitions.normal};
  
  ${media.tablet`
    width: ${({ isMeasuring }) => isMeasuring ? '32px' : '36px'};
    height: ${({ isMeasuring }) => isMeasuring ? '32px' : '36px'};
  `}
  
  ${Button}:hover & {
    transform: scale(1.1);
  }
`;

const LoadingContainer = styled.div`
  position: relative;
  z-index: 2;
`;

export interface IStartButton {
  /**
   *  Displays a spinner when isLoading is true
   */
  isLoading: boolean;
  /**
   *  function triggered on button click
   */
  onClick: () => void;
  /**
   *  Displays whether the current session state is measuring
   */
  isMeasuring: boolean;
  /**
   *  Current progress (0-100) for the circular progress indicator
   */
  progress?: number;
  /**
   *  Current seconds elapsed
   */
  currentSeconds?: number;
  /**
   *  Total duration in seconds
   */
  totalSeconds?: number;
}

const StartButton = ({ 
  isLoading, 
  onClick, 
  isMeasuring, 
  progress = 0,
  currentSeconds = 0,
  totalSeconds = 60
}: IStartButton) => {
  // Calculate progress percentage
  const progressPercentage = isMeasuring && totalSeconds > 0 
    ? Math.min((currentSeconds / totalSeconds) * 100, 100) 
    : 0;
  
  // Calculate circle properties
  const radius = 36; // Adjusted for new size
  const circumference = 2 * Math.PI * radius;
  
  return (
    <Container>
      <ProgressRing progress={progressPercentage}>
        <BackgroundCircle
          cx="50%"
          cy="50%"
          r={radius}
        />
        <ProgressCircle
          cx="50%"
          cy="50%"
          r={radius}
          circumference={circumference}
          progress={progressPercentage}
          isMeasuring={isMeasuring}
        />
      </ProgressRing>
      
      {isLoading ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : (
        <Button onClick={onClick} isMeasuring={isMeasuring}>
          <Icon src={isMeasuring ? Stop : Play} isMeasuring={isMeasuring} />
        </Button>
      )}
    </Container>
  );
};

export default StartButton;
